/**
 * Session Timeout Warning Component
 * Warns users about session timeout and provides options
 */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    LinearProgress,
    Alert,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Warning as WarningIcon,
    Refresh as RefreshIcon,
    Logout as LogoutIcon,
    Close as CloseIcon,
    Timer as TimerIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import useSessionManager from '../hooks/useSessionManager';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: '12px 24px',
}));

const SessionTimeoutWarning = () => {
    const { user } = useSelector((state) => state.auth);
    const {
        sessionInfo,
        getTimeUntilExpiry,
        isSessionHealthy,
        refreshSession,
        clearSession
    } = useSessionManager();

    const [open, setOpen] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const [countdown, setCountdown] = useState(0);

    const timeUntilExpiry = getTimeUntilExpiry();
    const isHealthy = isSessionHealthy();

    // Check if warning should be shown
    useEffect(() => {
        if (timeUntilExpiry && timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
            setOpen(true);
            setTimeRemaining(timeUntilExpiry);
        } else {
            setOpen(false);
        }
    }, [timeUntilExpiry]);

    // Update countdown
    useEffect(() => {
        if (open && timeRemaining > 0) {
            const interval = setInterval(() => {
                setTimeRemaining(prev => {
                    const newTime = prev - 1000;
                    if (newTime <= 0) {
                        setOpen(false);
                        clearSession();
                        return 0;
                    }
                    return newTime;
                });
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [open, timeRemaining, clearSession]);

    // Update countdown display
    useEffect(() => {
        if (timeRemaining > 0) {
            const minutes = Math.floor(timeRemaining / 60000);
            const seconds = Math.floor((timeRemaining % 60000) / 1000);
            setCountdown(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
    }, [timeRemaining]);

    // Handle refresh session
    const handleRefresh = async() => {
        setRefreshing(true);
        try {
            await refreshSession();
            setOpen(false);
        } catch (error) {
            console.error('Session refresh failed:', error);
        } finally {
            setRefreshing(false);
        }
    };

    // Handle logout
    const handleLogout = () => {
        setOpen(false);
        clearSession();
    };

    // Handle close
    const handleClose = () => {
        setOpen(false);
    };

    // Get progress value
    const getProgressValue = () => {
        if (!timeRemaining) return 0;
        const total = 5 * 60 * 1000; // 5 minutes
        return Math.max(0, Math.min(100, (timeRemaining / total) * 100));
    };

    // Get progress color
    const getProgressColor = () => {
        const progress = getProgressValue();
        if (progress < 20) return 'error';
        if (progress < 50) return 'warning';
        return 'primary';
    };

    return ( <
        StyledDialog open = { open }
        onClose = { handleClose }
        maxWidth = "sm"
        fullWidth disableEscapeKeyDown disableBackdropClick >
        <
        DialogTitle >
        <
        Box display = "flex"
        alignItems = "center"
        gap = { 2 } >
        <
        WarningIcon color = "warning"
        sx = {
            { fontSize: 32 } }
        /> <
        Box flex = { 1 } >
        <
        Typography variant = "h6" >
        Session Expiring Soon <
        /Typography> <
        Typography variant = "body2"
        color = "text.secondary" >
        Your session will expire in { countdown } <
        /Typography> <
        /Box> <
        IconButton onClick = { handleClose }
        size = "small"
        edge = "end" >
        <
        CloseIcon / >
        <
        /IconButton> <
        /Box> <
        /DialogTitle>

        <
        DialogContent >
        <
        Box mb = { 3 } >
        <
        Alert severity = "warning"
        sx = {
            { mb: 2 } } >
        <
        Typography variant = "body2" >
        Your session is about to expire due to inactivity.You can extend your session or logout safely. <
        /Typography> <
        /Alert>

        <
        Box mb = { 2 } >
        <
        Box display = "flex"
        justifyContent = "space-between"
        mb = { 1 } >
        <
        Typography variant = "body2"
        color = "text.secondary" >
        Time Remaining <
        /Typography> <
        Typography variant = "body2"
        color = "text.secondary" > { countdown } <
        /Typography> <
        /Box> <
        LinearProgress variant = "determinate"
        value = { getProgressValue() }
        color = { getProgressColor() }
        sx = {
            { height: 6, borderRadius: 3 } }
        /> <
        /Box>

        <
        Typography variant = "body2"
        color = "text.secondary" >
        <
        strong > What happens next ? < /strong> <
        /Typography> <
        Typography variant = "body2"
        color = "text.secondary"
        sx = {
            { mt: 1 } } > •If you don 't take action, you'
        ll be automatically logged out• Any unsaved work may be lost• You 'll need to log in again to continue <
        /Typography> <
        /Box> <
        /DialogContent>

        <
        DialogActions sx = {
            { p: 3, pt: 0 } } >
        <
        StyledButton variant = "outlined"
        color = "error"
        onClick = { handleLogout }
        startIcon = { < LogoutIcon / > } >
        Logout Now <
        /StyledButton>

        <
        StyledButton variant = "contained"
        onClick = { handleRefresh }
        disabled = { refreshing }
        startIcon = { refreshing ? < LinearProgress size = { 16 } /> : <RefreshIcon / > } >
        { refreshing ? 'Extending...' : 'Extend Session' } <
        /StyledButton> <
        /DialogActions> <
        /StyledDialog>
    );
};

export default SessionTimeoutWarning;